/**
 * @fileoverview Organization settings page for admin users.
 * Allows admins to edit org name/slug, manage members (invite, update role, remove),
 * and view the member list.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Loading from '../components/Loading';
import Toast from '../components/Toast';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import BusinessForm from '../components/BusinessForm';
import AuthorizedInvoiceEmails from '../components/AuthorizedInvoiceEmails';

/**
 * OrgSettings page component.
 * Two-column layout: org details sidebar + members/invitations main area.
 * Only accessible to users with 'admin' role in the current org.
 *
 * @returns {JSX.Element} The org settings page
 */
function OrgSettings() {
  const { activeOrg } = useAuth();
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [autoFileEmailInvoices, setAutoFileEmailInvoices] = useState(false);
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [invitations, setInvitations] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const orgId = activeOrg?.id;

  /**
   * Load organization details and member list.
   */
  const loadOrgData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [orgResp, membersResp, invitationsResp] = await Promise.all([
        api.getOrganization(orgId),
        api.getOrgMembers(orgId),
        api.getOrgInvitations(orgId),
      ]);
      setOrgName(orgResp.data.name || '');
      setOrgSlug(orgResp.data.slug || '');
      setAutoFileEmailInvoices(!!orgResp.data.auto_file_email_invoices);
      setMembers(membersResp.data || []);
      setInvitations(invitationsResp.data || []);
    } catch {
      setToast({ message: 'Failed to load organization data', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadOrgData();
  }, [loadOrgData]);

  /**
   * Filtered members based on search text.
   */
  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return members;
    const query = memberSearch.toLowerCase();
    return members.filter(
      (member) =>
        (member.display_name || '').toLowerCase().includes(query) ||
        (member.username || '').toLowerCase().includes(query) ||
        (member.email || '').toLowerCase().includes(query)
    );
  }, [members, memberSearch]);

  /**
   * Handle org name/slug update form submission.
   *
   * @param {Event} e - Form submit event
   */
  const handleUpdateOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateOrganization(orgId, { name: orgName, slug: orgSlug });
      setToast({ message: 'Organization updated successfully', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update organization', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Toggle the "auto-file emailed invoices" org setting.
   * Optimistically updates the local state, then persists via the org PUT
   * endpoint (which accepts an ``auto_file_email_invoices`` boolean). On
   * API failure the local state is reverted and an error toast is shown so
   * the user is not left with a stale checkbox that disagrees with the
   * server.
   */
  const handleToggleAutoFile = async () => {
    const previous = autoFileEmailInvoices;
    const next = !previous;
    setAutoFileEmailInvoices(next);
    try {
      await api.updateOrganization(orgId, {
        name: orgName,
        slug: orgSlug,
        auto_file_email_invoices: next,
      });
      setToast({
        message: next
          ? 'Emailed invoices with high confidence will now auto-file'
          : 'Emailed invoices will land in the OCR review queue',
        type: 'success',
      });
    } catch (err) {
      setAutoFileEmailInvoices(previous);
      setToast({
        message: err.response?.data?.error || 'Failed to update auto-file setting',
        type: 'error',
      });
    }
  };

  /**
   * Handle member invite form submission.
   *
   * @param {Event} e - Form submit event
   */
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      setToast({ message: 'Email is required', type: 'error' });
      return;
    }
    setInviting(true);
    try {
      await api.sendOrgInvitation(orgId, { email: inviteEmail, role: inviteRole });
      setInviteEmail('');
      setInviteRole('viewer');
      setToast({ message: 'Invitation sent successfully', type: 'success' });
      loadOrgData();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to send invitation', type: 'error' });
    } finally {
      setInviting(false);
    }
  };

  /**
   * Handle revoking a pending invitation.
   *
   * @param {number} invitationId - The invitation record ID
   * @param {string} email - The invited email for confirmation
   */
  const handleRevokeInvitation = async (invitationId, email) => {
    setConfirmAction({
      title: 'Revoke Invitation',
      message: `Revoke the invitation to ${email}?`,
      onConfirm: async () => {
        try {
          await api.revokeOrgInvitation(orgId, invitationId);
          setToast({ message: 'Invitation revoked', type: 'success' });
          loadOrgData();
        } catch (err) {
          setToast({ message: err.response?.data?.error || 'Failed to revoke invitation', type: 'error' });
        }
        setConfirmAction(null);
      },
    });
  };

  /**
   * Handle role change for a member.
   *
   * @param {number} memberId - The membership record ID
   * @param {string} newRole - The new role to assign
   */
  const handleRoleChange = async (memberId, newRole) => {
    try {
      await api.updateOrgMember(orgId, memberId, { role: newRole });
      setToast({ message: 'Member role updated', type: 'success' });
      loadOrgData();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update role', type: 'error' });
    }
  };

  /**
   * Handle member removal.
   *
   * @param {number} memberId - The membership record ID
   * @param {string} memberName - Display name for confirmation
   */
  const handleRemoveMember = (memberId, memberName) => {
    setConfirmAction({
      title: 'Remove Member',
      message: `Remove ${memberName} from this organization? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await api.removeOrgMember(orgId, memberId);
          setToast({ message: 'Member removed', type: 'success' });
          loadOrgData();
        } catch (err) {
          setToast({ message: err.response?.data?.error || 'Failed to remove member', type: 'error' });
        }
        setConfirmAction(null);
      },
    });
  };

  if (loading) {
    return (
      <div className="org-settings">
        <div className="page-header">
          <h1 className="page-title">Organization Settings</h1>
        </div>
        <Loading variant="spinner" message="Loading organization settings..." />
      </div>
    );
  }

  return (
    <div className="org-settings">
      <div className="page-header org-settings__header">
        <div>
          <h1 className="page-title">Organization Settings</h1>
          <p className="page-subtitle">Manage your organization details, members, and invitations</p>
        </div>
        <div className="org-settings__header-details">
          <div className="org-settings__details-item">
            <span className="org-settings__details-label">Organization</span>
            <span className="org-settings__details-value">{orgName}</span>
          </div>
          <div className="org-settings__details-item">
            <span className="org-settings__details-label">Slug</span>
            <span className="org-settings__details-value" style={{ fontFamily: 'var(--font-m)' }}>{orgSlug}</span>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditingDetails(true)}>Edit</button>
        </div>
      </div>

      {/* Businesses & Locations */}
      <div style={{ marginBottom: 'var(--sp5)' }}>
        <h2 className="org-settings__section-title" style={{ fontSize: '1.15rem', marginBottom: 'var(--sp4)' }}>
          Businesses & Locations
        </h2>
        <BusinessForm />
      </div>

      {/* Emailed invoice auto-file toggle */}
      <div className="card org-settings__card">
        <h2 className="org-settings__section-title" style={{ fontSize: '1.15rem' }}>
          Emailed invoice auto-file
        </h2>
        <label className="org-settings__toggle">
          <input
            type="checkbox"
            role="switch"
            checked={autoFileEmailInvoices}
            onChange={handleToggleAutoFile}
            aria-describedby="auto-file-email-help"
          />
          <span className="org-settings__toggle-text">
            Automatically file emailed invoices when Claude Vision confidence is &ge; 85%
          </span>
        </label>
        <p id="auto-file-email-help" className="hint">
          When off (default), all emailed invoices land in the OCR review queue for manual approval.
          When on, high-confidence extractions skip the queue and are filed straight to your invoice list.
          You can always turn this back off.
        </p>
      </div>

      {/* Authorized Invoice Email Addresses (v3.0 Phase 3.5) */}
      <AuthorizedInvoiceEmails orgId={orgId} />

      {/* Edit Org Details Modal */}
      <Modal isOpen={editingDetails} onClose={() => setEditingDetails(false)} title="Edit Organization Details" size="md">
        <form onSubmit={async (e) => { await handleUpdateOrg(e); setEditingDetails(false); }}>
          <div className="field">
            <label className="label" htmlFor="org-name">Organization Name <span className="req">*</span></label>
            <input id="org-name" className="input" type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)} required maxLength={200} />
          </div>
          <div className="field">
            <label className="label" htmlFor="org-slug">URL Slug <span className="req">*</span></label>
            <input id="org-slug" className="input" type="text" value={orgSlug} onChange={(e) => setOrgSlug(e.target.value)} required maxLength={100} pattern="[a-z0-9][a-z0-9\-]*[a-z0-9]" title="Lowercase letters, numbers, and hyphens only" />
            <span className="hint">Lowercase letters, numbers, and hyphens</span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp2)', marginTop: 'var(--sp4)' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            <button type="button" className="btn btn-ghost" onClick={() => setEditingDetails(false)}>Cancel</button>
          </div>
        </form>
      </Modal>

      {/* Members Card — invite form inline + table */}
      <div className="card org-settings__card">
        {/* Invite bar */}
        <form onSubmit={handleInvite} className="org-settings__invite-bar">
          <h3 className="org-settings__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none', whiteSpace: 'nowrap' }}>
            Members ({members.length})
          </h3>
          <input
            id="invite-email"
            className="input"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="Invite by email..."
            aria-label="Invite email"
          />
          <select
            id="invite-role"
            className="input org-settings__role-select"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            aria-label="Invite role"
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" className="btn btn-secondary btn-sm" disabled={inviting} style={{ whiteSpace: 'nowrap' }}>
            {inviting ? 'Sending...' : 'Invite'}
          </button>
          {members.length > 3 && (
            <input
              className="input org-settings__search-input"
              type="text"
              placeholder="Search..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              aria-label="Search members"
            />
          )}
        </form>

        {/* Members Table */}
            {filteredMembers.length === 0 ? (
              <div className="empty-state">
                <span className="empty-state__title">No members found</span>
              </div>
            ) : (
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">Role</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((member) => (
                      <tr key={member.id}>
                        <td>{member.display_name || member.username}</td>
                        <td>{member.email}</td>
                        <td>
                          <select
                            className="input org-settings__role-select"
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value)}
                            aria-label={`Role for ${member.display_name || member.username}`}
                          >
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleRemoveMember(member.id, member.display_name || member.username)}
                            aria-label={`Remove ${member.display_name || member.username}`}
                            style={{ color: 'var(--error)' }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pending Invitations — inline below members */}
            {invitations.length > 0 && (
              <>
                <h3 className="org-settings__section-title" style={{ marginTop: 'var(--sp5)' }}>
                  Pending Invitations ({invitations.length})
                </h3>
                <div className="tbl-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Email</th>
                        <th scope="col">Role</th>
                        <th scope="col">Invited By</th>
                        <th scope="col">Expires</th>
                        <th scope="col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.map((inv) => (
                        <tr key={inv.id}>
                          <td>{inv.email}</td>
                          <td><span className="badge">{inv.role}</span></td>
                          <td>{inv.invited_by_name || 'Unknown'}</td>
                          <td>{new Date(inv.expires_at).toLocaleDateString()}</td>
                          <td>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleRevokeInvitation(inv.id, inv.email)}
                              aria-label={`Revoke invitation to ${inv.email}`}
                              style={{ color: 'var(--error)' }}
                            >
                              Revoke
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
      </div>

      {/* Confirm Modal */}
      {confirmAction && (
        <ConfirmModal
          isOpen={true}
          title={confirmAction.title}
          message={confirmAction.message}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default OrgSettings;
